import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Submission } from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import { Badge, Button, Input, useToast } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { formatDateTime } from '../../shared/format';
import { labelOf } from './field-input';
import { describeSubmissionValue, displayStatus } from './submission-display';

/**
 * Eén ingediende aanvraag met de knoppen om te beslissen, zoals de formulierbouwer ze onder
 * "Inzendingen" toont.
 *
 * Dit stond vroeger in `forms-admin-page.tsx`, naast het oude formulierenbeheer en de oude inbox.
 * Die zijn sinds het herontwerp (BHR-7) vervangen door `form-builder-page.tsx` en
 * `submissions-inbox.tsx`; alleen deze kaart en `describe` werden nog gebruikt (OPR-1).
 */
export function SubmissionRow({ submission, formId }: { submission: Submission; formId?: string }) {
  const { t, i18n } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const decide = useMutation({
    mutationFn: async (decision: 'approve' | 'reject') => {
      const { error } = await api.POST('/api/v1/forms/submissions/{submissionId}/decide', {
        params: { path: { submissionId: submission.id } },
        body: { decision, note },
      });
      if (error) throw error;
    },
    onSuccess: (_result, decision) => {
      void queryClient.invalidateQueries({ queryKey: ['forms'] });
      if (formId) {
        void queryClient.invalidateQueries({ queryKey: ['forms', 'submissions', formId] });
      }
      toast.show({
        tone: 'success',
        title: decision === 'approve' ? t('formsAdmin.approved') : t('formsAdmin.rejected'),
      });
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  return (
    <div data-testid="submission-row" className="rounded-md bg-surface-sunk p-element">
      <div className="flex flex-wrap items-start justify-between gap-element">
        <div className="min-w-0">
          <p className="font-heading text-content font-semibold text-text-primary">
            {submission.personName}
            {submission.employeeNumber ? ` · ${submission.employeeNumber}` : ''}
          </p>
          <p className="text-small text-text-muted">
            {formatDateTime(submission.submittedAt)} ·{' '}
            {t('formsAdmin.versionLive', { number: submission.versionNumber })}
          </p>
        </div>
        <Badge
          tone={
            submission.status === 'approved'
              ? 'success'
              : submission.status === 'rejected'
                ? 'danger'
                : displayStatus(submission) === 'received'
                  ? 'neutral'
                  : 'warning'
          }
        >
          {t(`forms.status.${displayStatus(submission)}`)}
        </Badge>
      </div>

      {/* De waarden met de labels van de versie waartegen is ingediend — niet van de nieuwste. */}
      <dl className="mt-element grid gap-1">
        {submission.fields.map((field) => (
          <div key={field.key} className="flex flex-wrap gap-2">
            <dt className="text-small text-text-muted">
              {labelOf(field.label, i18n.language, field.key)}:
            </dt>
            <dd className="text-small text-text-primary">
              {describe(field, submission.data[field.key], i18n.language)}
            </dd>
          </div>
        ))}
      </dl>

      {submission.decisionNote ? (
        <p className="mt-element text-small text-text-secondary">
          {t('formsAdmin.decisionBy', {
            name: submission.decidedByName ?? '?',
            note: submission.decisionNote,
          })}
        </p>
      ) : null}

      {submission.canDecide ? (
        <div className="mt-element flex flex-col gap-element">
          <Input
            label={t('formsAdmin.note')}
            hint={t('formsAdmin.noteHint')}
            value={note}
            maxLength={500}
            onChange={(event) => setNote(event.target.value)}
            data-testid="submission-note"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              loading={decide.isPending}
              onClick={() => decide.mutate('approve')}
              data-testid="submission-approve"
            >
              <Check aria-hidden className="mr-2 h-4 w-4" strokeWidth={2.5} />
              {t('formsAdmin.approve')}
            </Button>
            <Button
              variant="secondary"
              loading={decide.isPending}
              onClick={() => decide.mutate('reject')}
              data-testid="submission-reject"
            >
              <X aria-hidden className="mr-2 h-4 w-4" strokeWidth={2.5} />
              {t('formsAdmin.reject')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Eén waarde in mensentaal; zie `describeSubmissionValue`. */
export const describe = describeSubmissionValue;
